import { type EditorView } from 'prosemirror-view'
import { Fragment, Node, Slice } from 'prosemirror-model'
import { MAX_LIST_DEPTH, schema } from 'prose-core'
import { Plugin } from 'prosemirror-state'

// NOTE: spreads every node that is pasted into an empty list item into a separate list item
export const SpreadPasteIntoListItems = new Plugin({
  props: {
    handlePaste: (
      {
        dispatch,
        state: {
          selection: { $from, from, to },
          tr,
        },
      }: EditorView,
      event: Event,
      slice: Slice,
    ) => {
      if ($from.depth < 1) return false

      const maybeListItem = $from.node($from.depth - 1)
      if (maybeListItem.type !== schema.nodes.list_item) return false
      if (
        maybeListItem.childCount > 1 || // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (maybeListItem.firstChild!.type.groups.includes('textWrap') && maybeListItem.firstChild!.childCount)
      )
        return false // only on empty list items

      let newContent: Node[] = []
      let sliceFragment = slice.content
      const openStart = slice.openStart
      const openEnd = slice.openEnd
      if (openStart > 0) {
        for (let depth = 0; depth < openEnd; depth++) {
          if (depth >= openStart - 1) {
            newContent = addFragmentToContent(sliceFragment, newContent, maybeListItem.attrs.depth)
          }
          if (sliceFragment.firstChild?.content) {
            sliceFragment = sliceFragment.firstChild.content
            if (sliceFragment.content.every((node) => node.isText)) break
          } else {
            break
          }
        }
      } else {
        newContent = addFragmentToContent(sliceFragment, newContent, maybeListItem.attrs.depth)
      }

      tr.replaceRange(from, to, new Slice(Fragment.from(newContent), 0, 0))
      dispatch(tr)

      return true
    },
  },
})

const addFragmentToContent = (fragment: Fragment, content: Node[], possibleParentListDepth: number) => {
  let inlineTextNodes: Node[] = []
  for (let nodeIndex = 0; nodeIndex < fragment.childCount; nodeIndex++) {
    const node = fragment.child(nodeIndex)
    if (node.isText) {
      inlineTextNodes.push(node)
    } else {
      if (inlineTextNodes.length > 0) {
        content.push(
          schema.nodes.list_item.create(
            {
              depth: possibleParentListDepth,
            },
            schema.nodes.paragraph.create({}, inlineTextNodes),
          ),
        )
        inlineTextNodes = []
      }
      if (node.type.groups.includes('list')) {
        // NOTE: coerce list depth of existing lists
        node.content.forEach((li) =>
          content.push(
            schema.nodes.list_item.create(
              { depth: Math.min(li.attrs.depth + possibleParentListDepth, MAX_LIST_DEPTH) },
              li.content,
            ),
          ),
        )
      } else if (node.type.groups.includes('block')) {
        content.push(
          schema.nodes.list_item.create(
            {
              depth: node.attrs.depth
                ? Math.min(node.attrs.depth + possibleParentListDepth, MAX_LIST_DEPTH)
                : possibleParentListDepth,
            },
            node,
          ),
        )
      } else if (node.type === schema.nodes.br) {
        continue
      } else {
        content.push(node)
      }
    }
  }

  if (inlineTextNodes.length > 0) {
    content.push(
      schema.nodes.list_item.create(
        {
          depth: possibleParentListDepth,
        },
        schema.nodes.paragraph.create({}, inlineTextNodes),
      ),
    )
  }

  return content
}
